import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import * as staffService from "../../services/subAdmin.services";
import Breadcrums from "../../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import {
  ATLEAST_ONE_CAPITAL_REGEX, ATLEAST_ONE_NUMBER_REGEX,
  ATLEAST_ONE_SMALL_REGEX, ATLEAST_ONE_SPECIAL_CHARACTER_REGEX,
  PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, SWAL_SETTINGS,
} from "../../../../utils/Constants";
import { capitalizeFirstLetter, globalLoader, handleServerValidations } from "../../../../utils/commonfunction";
import * as Yup from "yup";

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const IMAGE_SIZE_ERROR = "Image size should not exceed 5 MB.";

const SubAdminAdd = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [newshow, setNewShow] = useState(false);
  const [conformshow, setConformShow] = useState(false);
  const [previewimage, setPreviewImage] = useState("");
  const [roleData, setRoleData] = useState([]);

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: "Sub Admins", url: "/admin/staff/list/1" },
    { title: t("link_add"), url: "" },
  ];

  useEffect(() => {
    staffService.RoleOptions().then((response) => {
      setRoleData(response?.data || []);
    });
  }, []);

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required("First name is required"),
    last_name: Yup.string().required("Last name is required"),
    email: Yup.string().email("Invalid email address").required("Email is required"),
    password: Yup.string()
      .required("Password is required")
      .matches(ATLEAST_ONE_SMALL_REGEX, "Password should have at least 1 lowercase letter")
      .matches(ATLEAST_ONE_CAPITAL_REGEX, "Password should have at least 1 capital letter")
      .matches(ATLEAST_ONE_NUMBER_REGEX, "Password should have at least 1 number")
      .matches(ATLEAST_ONE_SPECIAL_CHARACTER_REGEX, "Password should have at least 1 special character")
      .min(PASSWORD_MIN_LENGTH, `Password should be at least ${PASSWORD_MIN_LENGTH} characters`)
      .max(PASSWORD_MAX_LENGTH, `Password cannot be more than ${PASSWORD_MAX_LENGTH} characters`),
    confirm_password: Yup.string().required("Confirm password is required").oneOf([Yup.ref("password"), null], "Passwords don't match"),
    permission_role: Yup.string().required("Role is required"),
    files: Yup.mixed()
      .test("fileType", "Please select a valid image.", (value) => {
        if (!value) return true;
        const supportedFormats = ["image/jpeg", "image/png", "image/gif", "image/webp"];
        return supportedFormats.includes(value.type);
      })
      .test("fileSize", IMAGE_SIZE_ERROR, (value) => {
        if (!value) return true;
        return value.size <= MAX_IMAGE_SIZE;
      }),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { first_name: "", last_name: "", email: "", password: "", confirm_password: "", permission_role: "", files: "" },
    validateOnBlur: false,
    validationSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      globalLoader(true);
      setSubmitted(true);
      const formData = new FormData();
      formData.append("profile_image", values.files);
      formData.append("first_name", values.first_name);
      formData.append("last_name", values.last_name);
      formData.append("email", values.email);
      formData.append("password", values.password);
      formData.append("confirm_password", values.confirm_password);
      formData.append("permission_role", values.permission_role);
      staffService.Add(formData).then((response) => {
        setSubmitting(false);
        globalLoader(false);
        if (response.success) {
          Swal.fire({ icon: "success", text: response.message, ...SWAL_SETTINGS });
          resetForm({ values: "" });
          navigate("/admin/staff/list/1");
        } else {
          Swal.fire({ icon: "error", text: handleServerValidations(response), ...SWAL_SETTINGS });
          setSubmitted(false);
        }
      }).catch(() => { setSubmitting(false); globalLoader(false); });
    },
  });

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <form onSubmit={formik.handleSubmit}>
        <div className="row row-sm">
          <div className="col-lg-12 col-md-12 animation_fade">
            <div className="card custom-card">
              <div className="card-body">
                <h6 className="main-content-label mb-3">Add Sub Admin</h6>
                <div className="row row-sm">

                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">First Name <span className="requirestar">*</span></label>
                    <input name="first_name" type="text" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.first_name} className="form-control" />
                    {formik.touched.first_name && formik.errors.first_name && <span className="text-danger d-flex">{formik.errors.first_name}</span>}
                  </div>

                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">Last Name <span className="requirestar">*</span></label>
                    <input name="last_name" type="text" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.last_name} className="form-control" />
                    {formik.touched.last_name && formik.errors.last_name && <span className="text-danger d-flex">{formik.errors.last_name}</span>}
                  </div>

                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">Email <span className="requirestar">*</span></label>
                    <input name="email" type="text" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.email} className="form-control" />
                    {formik.touched.email && formik.errors.email && <span className="text-danger d-flex">{formik.errors.email}</span>}
                  </div>

                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">Password <span className="requirestar">*</span></label>
                    <div className="input-group cp">
                      <input name="password" type={newshow ? "text" : "password"} onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.password} className="form-control" />
                      <span className="input-group-text" onClick={() => setNewShow(!newshow)}>
                        <i className={`far fa-eye${newshow ? "" : "-slash"}`}></i>
                      </span>
                    </div>
                    {formik.touched.password && formik.errors.password && <span className="text-danger d-flex">{formik.errors.password}</span>}
                  </div>

                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">Confirm Password <span className="requirestar">*</span></label>
                    <div className="input-group cp">
                      <input name="confirm_password" type={conformshow ? "text" : "password"} onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.confirm_password} className="form-control" />
                      <span className="input-group-text" onClick={() => setConformShow(!conformshow)}>
                        <i className={`far fa-eye${conformshow ? "" : "-slash"}`}></i>
                      </span>
                    </div>
                    {formik.touched.confirm_password && formik.errors.confirm_password && <span className="text-danger d-flex">{formik.errors.confirm_password}</span>}
                  </div>

                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">Role <span className="requirestar">*</span></label>
                    <div className="select-down-arrow">
                      <select name="permission_role" className="form-control" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.permission_role || ""}>
                        <option value="" disabled>Select Role</option>
                        {roleData.map((item) => (
                          <option key={item._id} value={item._id}>{capitalizeFirstLetter(item.title)}</option>
                        ))}
                      </select>
                    </div>
                    {formik.touched.permission_role && formik.errors.permission_role && <span className="text-danger d-flex">{formik.errors.permission_role}</span>}
                  </div>

                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">Profile Image</label>
                    <input className="form-control imgInput" name="files" accept="image/*" type="file"
                      onChange={(e) => {
                        formik.setFieldValue("files", e.currentTarget.files[0] || "");
                        if (e.currentTarget.files.length === 1) setPreviewImage(URL.createObjectURL(e.currentTarget.files[0]));
                        else setPreviewImage("");
                      }} />
                    {formik.touched.files && formik.errors.files && <span className="text-danger d-flex">{formik.errors.files}</span>}
                  </div>

                  {previewimage && <div className="mb-4"><img src={previewimage} style={{ height: "100px" }} alt="" /></div>}

                  <div className="mt-5">
                    <button className="btn btn-info mr-2" type="submit" disabled={submitted}>{t("btn_submit")}</button>
                    <button className="btn ripple btn-secondary" type="button" disabled={submitted} onClick={() => navigate(-1)}>{t("btn_cancel")}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default SubAdminAdd;
